import {styled} from '@mui/material/styles';
import {useCallback} from 'react';

import {CustomDialogItem} from 'filters/features/FilterDialog/types';
import {PeriodType, InclusionDateType} from 'modules/decisionsNext/api/types';
import {DateRangeFieldValue} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/types';

import {todayString} from 'utils/dateUtils';
import {formatDateRangeValue} from 'features/GlobalFilterProvider/filters/period/utils';
import {useEditedFilterContext} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider/EditedFilterProvider';

import {DateTypeRadioGroup} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/components/DateTypeRadioGroup';
import {InForceSelectionGroup} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/components/InForceSelectionGroup';
import {PeriodTypeRadioGroup} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/components/PeriodTypeRadioGroup';
import {FromToDatePicker} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/DateRangeBlock/FromToDatePicker';
import {AdditionalPeriodGroup} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/components/AdditionalPeriodGroup';
import {Block} from 'filters/features/FilterDialog/components/Block';

type DateRangeConfig = CustomDialogItem<DateRangeFieldValue>;

export const createDateRangeItem = (options: Omit<DateRangeConfig, 'Component'>): DateRangeConfig => {
  return {...options, Component: DateRangeItem};
};

const SContent = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

// FIXME: Display RHF validation errors instead of internal ones
const DateRangeItem: DateRangeConfig['Component'] = ({getValue, onChange}) => {
  const {filter, setFilter} = useEditedFilterContext();

  const value = getValue(filter);
  const props = {
    value,
    updateValue: useCallback(
      (newValue: Partial<DateRangeFieldValue>) => {
        setFilter(prev =>
          onChange(
            prev,
            formatDateRangeValue({
              ...value,
              ...newValue,
            })
          )
        );
      },
      [value, onChange, setFilter]
    ),
  };

  return (
    <Block label='Date Range'>
      <SContent>
        <DateTypeRadioGroup {...props} />

        {value.type == PeriodType.InForce ? (
          <InForceSelectionGroup {...props} />
        ) : (
          <>
            <PeriodTypeRadioGroup {...props} />
            {value.type === PeriodType.FromTo && (
              <FromToDatePicker
                from={value.from}
                to={value.to}
                maxDate={value.inclusionDateType !== InclusionDateType.Expiration ? todayString : undefined}
                onChange={(from, to) => {
                  props.updateValue({
                    type: PeriodType.FromTo,
                    from,
                    to,
                  } satisfies DateRangeFieldValue);
                }}
              />
            )}

            {value.inclusionDateType !== InclusionDateType.Transaction && <AdditionalPeriodGroup {...props} />}
          </>
        )}
      </SContent>
    </Block>
  );
};
